import {
  Component,
  EventEmitter,
  Inject,
  Injector,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  Optional,
  Output,
  TemplateRef,
  Type,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { DeleteConfirmationComponent } from '../delete-confirmation/delete-confirmation.component';
import { StateStorageItem } from '../../../core/services/state-storage-base.service';
import { DefaultItemRendererComponent } from './default-item-renderer.component';
import { Observable, Subscription } from 'rxjs';
import { DarkModeService } from '../../dark-mode/dark-mode.service';

export interface BaseStoredItem extends StateStorageItem {
  name: string;
  description?: string;
  category?: string;
}

export interface StoredItemConfig<T extends BaseStoredItem> {
  title: string;
  description: string;
  searchPlaceholder?: string;
  tabs?: StoredItemTab<T>[];
  useCategories?: boolean;
  itemCountLabel?: (count: number) => string;
  allowDelete?: boolean;
  onDelete?: (item: T) => void;
  defaultComponentRenderer?: Type<any>;
  itemTypeName?: string;
}

export interface StoredItemTab<T extends BaseStoredItem> {
  id: string;
  label: string;
  filter: (item: T) => boolean;
  emptyMessage?: string;
  emptyDescription?: string;
  showCategories?: boolean;
  showCreator?: boolean;
  allowDelete?: boolean;
}

@Component({
    selector: 'app-stored-items-browser',
    templateUrl: './stored-items-browser.component.html',
    styleUrls: ['./stored-items-browser.component.scss'],
    standalone: false
})
export class StoredItemsBrowserComponent<T extends BaseStoredItem>
  implements OnInit, OnDestroy, OnChanges
{
  @Input() items$!: Observable<T[]>;
  @Input() config!: StoredItemConfig<T>;
  @Input() customItemTemplate?: TemplateRef<any>;
  @Output() itemSelected = new EventEmitter<T>();
  @Output() cancelled = new EventEmitter<void>();

  searchQuery: string = '';
  activeTab: string = '';
  isLoading: boolean = true;

  private readonly STORAGE_KEY = 'stored-items-browser-last-tab';
  items: T[] = [];

  categories: { [key: string]: T[] } = {};
  filteredCategories: { [key: string]: T[] } = {};
  filteredItems: T[] = [];
  currentTabItems: T[] = [];

  // Component class for all items - computed once
  itemComponentClass: Type<any> = DefaultItemRendererComponent;

  private itemInjectors = new Map<string, Injector>();
  private itemsSubscription?: Subscription;

  constructor(
    @Optional() private dialogRef: MatDialogRef<StoredItemsBrowserComponent<T>>,
    private dialog: MatDialog,
    private injector: Injector,
    public darkModeService: DarkModeService,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public dialogData?: { items$: Observable<T[]>; config: StoredItemConfig<T> }
  ) {}

  ngOnInit() {
    // If opened as a dialog, use the injected data
    if (this.dialogData) {
      this.items$ = this.dialogData.items$;
      this.config = this.dialogData.config;
    }

    // Apply default tabs if not provided BEFORE subscribing
    if (!this.config.tabs) {
      this.config.tabs = this.getDefaultTabs();
    }

    if (this.config.tabs && this.config.tabs.length > 0) {
      this.activeTab = this.getDefaultTab();
    }

    // Determine the component class once from config
    this.itemComponentClass = this.config.defaultComponentRenderer || DefaultItemRendererComponent;

    // Subscribe to items observable AFTER tabs are configured
    if (this.items$) {
      this.isLoading = true;
      this.itemsSubscription = this.items$.subscribe((items) => {
        if (items === null) return;

        this.items = items;
        this.isLoading = false;
        // Re-evaluate default tab after items are loaded
        this.activeTab = this.getDefaultTab();
        this.applyFilters();
      });
    }
  }

  ngOnDestroy() {
    if (this.itemsSubscription) {
      this.itemsSubscription.unsubscribe();
    }
  }

  private getDefaultTabs<T extends BaseStoredItem>(): StoredItemTab<T>[] {
    const itemType = this.config.itemTypeName || 'Items';
    const itemTypeLower = itemType.toLowerCase();
    const itemTypeSingular = itemType.endsWith('s') ? itemType.slice(0, -1) : itemType;

    return [
      {
        id: 'built-in',
        label: `Pre-Built ${itemType}`,
        filter: (item: any) => item.scope === 'public',
        emptyMessage: `No pre-built ${itemTypeLower} available`,
        emptyDescription: `Check back later for curated ${itemTypeLower} from our team`,
        showCategories: true,
        showCreator: false,
        allowDelete: false,
      },
      {
        id: 'my-items',
        label: `My ${itemType}`,
        filter: (item: any) => item.scope === 'org',
        emptyMessage: `You haven't created any ${itemTypeLower} yet`,
        emptyDescription: `Start by creating your first ${itemTypeLower.replace(/s$/, '')} to save and reuse later`,
        showCategories: true,
        showCreator: true,
        allowDelete: true,
      },
    ];
  }

  ngOnChanges() {
    this.applyFilters();
  }

  createItemInjector(item: T): Injector {
    const cacheKey = `${item.id}-${this.activeTab}`;

    // Return cached injector if it exists
    if (this.itemInjectors.has(cacheKey)) {
      return this.itemInjectors.get(cacheKey)!;
    }

    // Get current tab configuration
    const activeTabConfig = this.getActiveTab();

    // Determine allowDelete: tab setting overrides global config
    const allowDelete =
      activeTabConfig?.allowDelete !== undefined
        ? activeTabConfig.allowDelete
        : this.config.allowDelete || false;

    // Determine showCreator: tab setting overrides default (true)
    const showCreator =
      activeTabConfig?.showCreator !== undefined ? activeTabConfig.showCreator : true;

    // Create new injector and cache it
    const injector = Injector.create({
      parent: this.injector,
      providers: [
        { provide: 'item', useValue: item },
        { provide: 'allowDelete', useValue: allowDelete },
        { provide: 'showCreator', useValue: showCreator },
        {
          provide: 'onItemSelected',
          useValue: (selectedItem: T) => this.onItemSelected(selectedItem),
        },
        { provide: 'onItemDeleted', useValue: (deletedItem: T) => this.deleteItem(deletedItem) },
      ],
    });

    this.itemInjectors.set(cacheKey, injector);
    return injector;
  }

  trackByItemId(index: number, item: T): string {
    return item.id;
  }

  applyFilters() {
    // Clear injector cache when filters change to prevent memory leaks
    this.itemInjectors.clear();

    if (!this.items) {
      this.filteredItems = [];
      this.categories = {};
      this.filteredCategories = {};
      return;
    }

    let tabItems = this.items;

    if (this.config.tabs && this.activeTab) {
      const activeTabConfig = this.config.tabs.find((tab) => tab.id === this.activeTab);
      if (activeTabConfig) {
        tabItems = this.items.filter(activeTabConfig.filter);
      }
    }

    this.currentTabItems = tabItems.sort((a, b) => a.name.localeCompare(b.name));

    const query = this.searchQuery.toLowerCase().trim();
    if (query) {
      tabItems = tabItems.filter((item) => {
        const displayName = this.getItemDisplayName(item).toLowerCase();
        return displayName.includes(query);
      });
    }

    this.filteredItems = tabItems;

    if (this.shouldShowCategories()) {
      this.buildCategories(tabItems);
    }
  }

  shouldShowCategories(): boolean {
    if (!this.config.useCategories) return false;
    if (!this.config.tabs || !this.activeTab) return this.config.useCategories === true;
    const activeTabConfig = this.config.tabs.find((tab) => tab.id === this.activeTab);
    return activeTabConfig?.showCategories !== false;
  }

  buildCategories(items: T[]) {
    if (!this.config.useCategories) {
      this.categories = {};
      this.filteredCategories = {};
      return;
    }

    this.categories = items.reduce(
      (acc, item) => {
        const category = item.category || 'Uncategorized';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(item);
        return acc;
      },
      {} as { [key: string]: T[] }
    );
    this.filteredCategories = this.categories;
  }

  onSearch() {
    this.applyFilters();
  }

  clearSearch() {
    this.searchQuery = '';
    this.applyFilters();
  }

  onItemSelected(item: T) {
    this.itemSelected.emit(item);
    if (this.dialogRef) {
      this.dialogRef.close(item);
    }
  }

  onCancel() {
    this.cancelled.emit();
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  deleteItem(item: T, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    if (!this.config.allowDelete) return;

    const confirmDialog = this.dialog.open(DeleteConfirmationComponent, {
      data: {
        title: 'Confirm Deletion',
        message: `Are you sure you want to delete "${item.name}"?`,
      },
    });

    confirmDialog.afterClosed().subscribe((result: boolean) => {
      if (result === true && this.config.onDelete) {
        this.config.onDelete(item);
      }
    });
  }

  getItemDisplayName(item: T): string {
    return item.name || 'Unnamed Item';
  }

  getItemCount(items: T[]): string {
    const count = items.length;
    if (this.config.itemCountLabel) {
      return this.config.itemCountLabel(count);
    }
    return `${count} items`;
  }

  getActiveTab(): StoredItemTab<T> | undefined {
    if (!this.config.tabs) return undefined;
    return this.config.tabs.find((tab) => tab.id === this.activeTab);
  }

  private getDefaultTab(): string {
    if (!this.config.tabs || this.config.tabs.length === 0) {
      return '';
    }

    // Get the last used tab from localStorage
    const lastUsedTab = localStorage.getItem(this.STORAGE_KEY);

    // Check if the last used tab still exists in current tabs
    if (lastUsedTab && this.config.tabs.some((tab) => tab.id === lastUsedTab)) {
      // Check if "my-items" tab has items, if the last used tab is "my-items"
      if (lastUsedTab === 'my-items') {
        const myItemsTab = this.config.tabs.find((tab) => tab.id === 'my-items');
        if (myItemsTab && this.items) {
          const myItems = this.items.filter(myItemsTab.filter);
          // If no items in "my items", default to "built-in" (pre-built)
          if (myItems.length === 0) {
            return 'built-in';
          }
        }
      }
      return lastUsedTab;
    }

    // If no last used tab or it doesn't exist, check if "my-items" has items
    const myItemsTab = this.config.tabs.find((tab) => tab.id === 'my-items');
    if (myItemsTab && this.items) {
      const myItems = this.items.filter(myItemsTab.filter);
      // If "my items" has no items, default to "built-in" (pre-built)
      if (myItems.length === 0) {
        return 'built-in';
      }
    }

    // Default to first tab
    return this.config.tabs[0].id;
  }

  private saveActiveTabToStorage(): void {
    localStorage.setItem(this.STORAGE_KEY, this.activeTab);
  }

  onTabChange(tabId: string): void {
    if (this.isLoading) return;
    this.activeTab = tabId;
    this.saveActiveTabToStorage();
    this.applyFilters();
  }
}
